// Avoid re-exports in favor of direct imports or entry-point files
/* eslint-disable no-barrel-files/no-barrel-files */
import { defineMessages, useIntl } from 'react-intl-next';

import {
	SPACE_PERMISSIONS_SETTINGS,
	SPACE_SETTINGS_EXPORT,
	SPACE_SETTINGS_STATUS_AND_APPROVALS,
	SPACE_SETTINGS_AVATARS,
} from '@confluence/named-routes/entry-points/routes.ts';
import { fg } from '@confluence/feature-gating';
import { expValEquals, expVal } from '@confluence/feature-experiments';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { ConfluenceEdition } from '@confluence/change-edition/entry-points/ConfluenceEdition.ts';
import { useSpaceKey } from '@confluence/space-utils/entry-points/useSpaceKey.ts';
import { useSpaceManagementOperations } from '@confluence/space-utils/entry-points/graphql.ts';

import { isNonNullable } from '../utils.ts';
import { KeysOfWebItemsWithExtraUrls } from '../helpers/webItem.ts';

import { useSpacePermissionsWebItems } from './useSpacePermissionsWebItems.tsx';
import { usePublicLinksWebItem } from './usePublicLinksWebItem.tsx';
import type {
	SpaceSettingsWebItemSectionsFragment_space,
	SpaceSettingsWebItemSectionsFragment_webItemSections,
} from './__types__/SpaceSettingsWebItemSectionsFragment.ts';
import type { SpaceSettingsWebItemSectionsInjector } from './SpaceSettingsWebItemSectionsInjector.ts';
import { useExportSpaceWebItem } from './useExportSpaceWebItem.tsx';
import { useMigratedWebItem } from './useMigratedWebItem.tsx';

export const AUTOMATION_WEBITEM_KEY =
	'com.atlassian.plugins.atlassian-connect-plugin:com.atlassian.addons.automation.confluence__confluence-space-automation-rules';
export const AUTOMATION_WEBITEM_SECTION_ID_V1 =
	'-system.space.tools-com.atlassian.addons.automation.confluence__space-automation';
export const AUTOMATION_WEBITEM_SECTION_ID_V2 = 'space-automation-section';
export const SPACE_PERMISSIONS_WEBITEM_SECTION_ID = '-system.space.tools-permissions';
export const MANAGE_SPACE_WEBITEM_SECTION_ID = '-system.space.tools-overview';
export const SPACE_DETAILS_WEBITEM_KEY = 'confluence.sections.space.tools.v2:spacedetails';
export const SPACE_DETAILS_PERSONAL_WEBITEM_KEY =
	'confluence.sections.space.tools.v2:spacedetails-personal';
export const MANAGE_CONTENT_WEBITEM_SECTION_ID = '-system.space.tools-contenttools';
export const CONTENT_MANAGER_WEBITEM_KEY = 'confluence.sections.space.tools.v2:content_manager';
export const ANALYTICS_PLUGIN_WEB_ITEM =
	'com.atlassian.plugins.atlassian-connect-plugin:com.addonengine.analytics__analytics-permissions-space-tools-tab';

export const isAutomationSection = (
	section: SpaceSettingsWebItemSectionsFragment_webItemSections,
) =>
	section?.id === AUTOMATION_WEBITEM_SECTION_ID_V1 ||
	section?.id === AUTOMATION_WEBITEM_SECTION_ID_V2;

const useManageSpaceWebItemSection: SpaceSettingsWebItemSectionsInjector = ({
	webItemSections,
}) => {
	// XXX Don't mutate the input webItemSections! May be shared across invocations.
	return webItemSections
		?.filter(isNonNullable)
		.map((section): SpaceSettingsWebItemSectionsFragment_webItemSections => {
			if (section.id === MANAGE_SPACE_WEBITEM_SECTION_ID) {
				// Creates a new manage space web item section without the space details web item.
				return {
					...section,
					items: section.items.filter(
						(item) =>
							item?.completeKey !== SPACE_DETAILS_WEBITEM_KEY &&
							item?.completeKey !== SPACE_DETAILS_PERSONAL_WEBITEM_KEY,
					),
				};
			} else {
				return section;
			}
		});
};

const useManageContentWebItemSection: SpaceSettingsWebItemSectionsInjector = ({
	webItemSections,
}) => {
	// XXX Don't mutate the input webItemSections! May be shared across invocations.

	return webItemSections
		?.filter(isNonNullable)
		.map((section): SpaceSettingsWebItemSectionsFragment_webItemSections => {
			if (section.id === MANAGE_CONTENT_WEBITEM_SECTION_ID) {
				// Creates a new manage content web item section without the content manager web item.
				return {
					...section,
					items: section.items.filter((item) => item?.completeKey !== CONTENT_MANAGER_WEBITEM_KEY),
				};
			} else {
				return section;
			}
		});
};

export const useAnalyticsPermissionsWebItem: SpaceSettingsWebItemSectionsInjector = ({
	space,
	webItemSections,
	isSpaceAdmin,
}) => {
	const { formatMessage } = useIntl();
	const { edition } = useSessionData();
	const spaceKey = useSpaceKey();
	const { canManageUsers } = useSpaceManagementOperations({
		spaceKey: spaceKey ?? '',
		skip: expVal('permissions_rbac_dependencies_callsites', 'isEnabled', false) ? false : undefined,
	});
	const canSeeAnalytics = expVal('permissions_rbac_dependencies_callsites', 'isEnabled', false)
		? canManageUsers
		: isSpaceAdmin;

	// i18n messages
	const i18n = defineMessages({
		analyticsPermissions: {
			id: 'space-settings.analytics-permissions.label',
			defaultMessage: 'Analytics permissions',
			description:
				'Label for the analytics permissions item in space settings, leading to the analytics permissions page.',
		},
	});

	// overrides the plugin analytics permissions link with native link if it is enabled
	if (webItemSections) {
		return webItemSections.map((section) => {
			if (section && section.id === SPACE_PERMISSIONS_WEBITEM_SECTION_ID) {
				// Filter out analytics item when user cannot see it (experiment+RBAC: MANAGE_USERS; else space admin)
				const itemsWithoutAnalyticsIfHidden = canSeeAnalytics
					? section.items
					: section.items.filter((item) => item?.completeKey !== ANALYTICS_PLUGIN_WEB_ITEM);
				// Check if the analytics item already exists
				const hasAnalyticsItem = itemsWithoutAnalyticsIfHidden.some(
					(item) => item && item.completeKey === ANALYTICS_PLUGIN_WEB_ITEM,
				);

				// Map over the items and update the URL if the analytics item exists
				const updatedItems = itemsWithoutAnalyticsIfHidden.map((item) => {
					if (space && item && item.completeKey === ANALYTICS_PLUGIN_WEB_ITEM) {
						const nativePermissionsURL = SPACE_PERMISSIONS_SETTINGS.toUrl({
							spaceKey: space.alias ? space.alias : space.key,
							tab: 'analytics',
						});
						return {
							...item,
							label: formatMessage(i18n.analyticsPermissions),
							url: nativePermissionsURL,
						};
					}
					return item;
				});

				// If the analytics item does not exist, inject it with control of experiment flag (and MANAGE_USERS when permissions_rbac_dependencies_callsites is on)
				// Note the id and completeKey is just copied from the original plugin web item
				if (
					!hasAnalyticsItem &&
					space &&
					edition === ConfluenceEdition.PREMIUM &&
					canSeeAnalytics &&
					expValEquals('ccir_deprecate_analytics_connect_app', 'isEnabled', true)
				) {
					const nativePermissionsURL = SPACE_PERMISSIONS_SETTINGS.toUrl({
						spaceKey: space.alias ? space.alias : space.key,
						tab: 'analytics',
					});
					const object = {
						id: 'com.addonengine.analytics__analytics-permissions-space-tools-tab',
						completeKey: ANALYTICS_PLUGIN_WEB_ITEM,
						label: formatMessage(i18n.analyticsPermissions),
						url: nativePermissionsURL,
						icon: null,
					};
					updatedItems.push(object);
				}

				return {
					...section,
					items: updatedItems,
				};
			}
			return section;
		});
	}
	return webItemSections;
};

export const usePdfExportNavigationItem: SpaceSettingsWebItemSectionsInjector = ({
	space,
	webItemSections,
}) => {
	if (!fg('confluence-fe-space-settings-enable-afm-pdf')) return webItemSections;
	if (!space?.alias && !space?.key) return webItemSections;

	const ExportUrl = SPACE_SETTINGS_EXPORT.toUrl({ spaceKey: space.alias || space.key });

	return webItemSections?.map((section) => {
		if (section?.id === MANAGE_SPACE_WEBITEM_SECTION_ID) {
			return {
				...section,
				items: section.items.map((item) =>
					item?.completeKey === KeysOfWebItemsWithExtraUrls.EXPORT_SPACE_WEBITEM_COMPLETE_KEY
						? { ...item, url: ExportUrl }
						: item,
				),
			};
		}
		return section;
	});
};

const AVATARS_AND_STATUS_COMPLETEKEY = 'confluence.sections.space.tools.v2:avatars-and-status';

const i18nStatusAndApprovals = defineMessages({
	statusAndApprovalsLabel: {
		id: 'space-settings.status-and-approvals.label',
		defaultMessage: 'Status and approvals',
		description:
			'Label for the Status and approvals item in space settings navigation, shown when the approvals EAP experiment is enabled.',
	},
	avatarsLabel: {
		id: 'space-settings.avatars.label',
		defaultMessage: 'Avatars',
		description:
			'Label for the Avatars item in space settings navigation, shown when the approvals EAP experiment is enabled.',
	},
});

const useStatusAndApprovalsWebItem: SpaceSettingsWebItemSectionsInjector = ({
	space,
	webItemSections,
}) => {
	const { formatMessage } = useIntl();

	if (!fg('confluence_approvals_space_settings_fg') || (!space?.alias && !space?.key)) {
		return webItemSections;
	}

	const spaceKey = space.alias || space.key;
	const statusAndApprovalsUrl = SPACE_SETTINGS_STATUS_AND_APPROVALS.toUrl({ spaceKey });
	const avatarsUrl = SPACE_SETTINGS_AVATARS.toUrl({ spaceKey });

	return webItemSections?.map((section) => {
		if (section?.id !== MANAGE_SPACE_WEBITEM_SECTION_ID) {
			return section;
		}

		const hasAvatarsAndStatusItem = section.items.some(
			(item) => item?.completeKey === AVATARS_AND_STATUS_COMPLETEKEY,
		);

		if (!hasAvatarsAndStatusItem) {
			return section;
		}

		const updatedItems = section.items.flatMap((item) => {
			if (item?.completeKey !== AVATARS_AND_STATUS_COMPLETEKEY) {
				return [item];
			}

			// Replace "Avatars and status" with "Status and approvals" and inject new "Avatars" item
			return [
				{
					...item,
					label: formatMessage(i18nStatusAndApprovals.statusAndApprovalsLabel),
					url: statusAndApprovalsUrl,
				},
				{
					id: 'space-settings-avatars',
					completeKey: 'confluence.sections.space.tools.v2:avatars',
					label: formatMessage(i18nStatusAndApprovals.avatarsLabel),
					url: avatarsUrl,
					icon: null,
				},
			];
		});

		return {
			...section,
			items: updatedItems,
		};
	});
};

export const useHideEditorConversionWebItem: SpaceSettingsWebItemSectionsInjector = ({
	webItemSections,
}) => {
	if (expVal('confluence_tinymce_deprecation_phase', 'value', 0) <= 1) {
		return webItemSections;
	}

	return webItemSections?.filter(isNonNullable).map((section) => ({
		...section,
		items: section.items.filter(
			(item) => item?.completeKey !== 'confluence.sections.space.tools.v2:editor-conversion',
		),
	}));
};

// Generic type that matches the shape of SpaceSettingsWebItemSectionsFragment$data
// This allows the function to accept data from any query that has the same structure
type SpaceSettingsWebItemSectionsQueryData = {
	readonly space?: {
		readonly alias?: string | null | undefined;
		readonly id?: string | null | undefined;
		readonly key?: string | null | undefined;
		readonly currentUser?: {
			readonly isAdmin?: boolean;
		} | null;
	} | null;
	readonly webItemSections?: ReadonlyArray<
		| {
				readonly cacheKey?: string | null | undefined;
				readonly id?: string | null | undefined;
				readonly label?: string | null | undefined;
				readonly items: ReadonlyArray<
					| {
							readonly completeKey?: string | null | undefined;
							readonly icon?:
								| {
										readonly path: string;
								  }
								| null
								| undefined;
							readonly id?: string | null | undefined;
							readonly label?: string | null | undefined;
							readonly url?: string | null | undefined;
					  }
					| null
					| undefined
				>;
		  }
		| null
		| undefined
	> | null;
};

export const useSpaceSettingsWebItemSectionsFromQuery = (
	data?: SpaceSettingsWebItemSectionsQueryData,
): ReturnType<SpaceSettingsWebItemSectionsInjector> => {
	const space = data?.space;
	const isSpaceAdmin = data?.space?.currentUser?.isAdmin;
	const injectors: SpaceSettingsWebItemSectionsInjector[] = [
		useManageSpaceWebItemSection,
		useManageContentWebItemSection,
		usePublicLinksWebItem,
		useSpacePermissionsWebItems,
		useExportSpaceWebItem,
		useMigratedWebItem,
		useAnalyticsPermissionsWebItem,
		usePdfExportNavigationItem,
		useHideEditorConversionWebItem,
		useStatusAndApprovalsWebItem,
	];

	const initialSections = (data?.webItemSections ??
		undefined) as ReturnType<SpaceSettingsWebItemSectionsInjector>;

	return injectors.reduce(
		(webItemSections, injector) =>
			injector({
				space: space as SpaceSettingsWebItemSectionsFragment_space | null | undefined,
				webItemSections,
				isSpaceAdmin: isSpaceAdmin ?? false,
			}),
		initialSections,
	);
};
